// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getErrorMessages } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo } from "@insite/mobius/Icon";
import { Tooltip } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer from "@vanmeter/App/Components/Cart/CartLineContainer";
import QtyNumbers, { QtyNumbersStyles } from "@vanmeter/App/Components/Product/QtyNumbers";
import ProductAvailability, { ProductAvailabilityStyles } from "@vanmeter/Overrides/Components/ProductAvailability";
import NotificationWarning from "@vanmeter/XDL/Icons/IconNotificationWarning";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    // XNG-Changes
    showBrand?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    // XNG-Changes
    configurationContainer?: InjectableCss;
    configurationOptionLabel?: TypographyProps;
    promotionNameLabel?: TypographyProps;
    qtyNumbersStyles?: QtyNumbersStyles;
    productAvailability?: ProductAvailabilityStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 6,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    quantityGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    extendedUnitNetPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-flow: wrap;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    // XNG-Changes
    showBrand = false,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    // XNG-Changes: add error messages same as in cart page
    const errorMessages = getErrorMessages(cart, cartLine, false);

    // XNG-Changes: Use CartLineContainer
    return (
        <CartLineContainer data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}>
            <CartLineContainer.Image>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                {/* XNG-Changes: add warning icon */}
                {errorMessages.length > 0 && (
                    <Tooltip
                        triggerComponent={<IconMemo src={NotificationWarning} color="warning" size={32} />}
                        text={errorMessages.join(", ")}
                    />
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {showBrand && cartLine.brand && (
                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                )}
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                <ProductPartNumbers
                    productNumber={cartLine.erpNumber}
                    customerProductNumber={cartLine.customerName}
                    manufacturerItem={cartLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                />
                {/* XNG-Changes: add QtyNumbers */}
                <QtyNumbers
                    minimumOrderQty={Number(cartLine.properties.minimumOrderQty) || 0}
                    extendedStyles={styles.qtyNumbersStyles}
                />
                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                    <StyledWrapper {...styles.configurationContainer}>
                        {cartLine.sectionOptions!.map(option => (
                            <StyledWrapper key={option.sectionOptionId}>
                                <Typography {...styles.configurationOptionLabel}>{option.sectionName}</Typography>
                                <Typography {...styles.configurationOptionText}>{option.optionName}</Typography>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                )}

                {promotions?.map((promotion, index) => (
                    <StyledWrapper key={promotion.id} data-test-selector="orderLineCardExpandedPromotionName">
                        <Typography {...styles.promotionNameLabel}>
                            {`${translate("Promotion")} #${index + 1}:`}
                        </Typography>
                        <Typography {...styles.promotionNameText}>{promotion.name}</Typography>
                    </StyledWrapper>
                ))}

                {/* XNG-Changes */}
                <ProductAvailability
                    productId={cartLine.productId!}
                    availability={cartLine.availability!}
                    unitOfMeasure={cartLine.unitOfMeasure}
                    trackInventory={cartLine.trackInventory}
                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                    extendedStyles={styles.productAvailability}
                    totalWarehouses={cartLine.qtyOnHand}
                    leadTime={cartLine.properties.leadTime}
                    additionalResults={cartLine.properties}
                />
                {/* {cartLine.isRestricted && (
                    <StyledWrapper>
                        <Typography
                            {...styles.cartLineErrorMessageText}
                            data-test-selector="cartline_restrictedMessage"
                        >
                            {translate("Restricted product")}
                        </Typography>
                    </StyledWrapper>
                )}
                {!cartLine.isActive && (
                    <StyledWrapper>
                        <Typography {...styles.cartLineErrorMessageText}>{translate("Inactive Product")}</Typography>
                    </StyledWrapper>
                )} */}
                {cart.showCostCode && cartLine.costCode && (
                    <StyledWrapper>
                        <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                        <Typography {...styles.costCodeValueText}>{cartLine.costCode}</Typography>
                    </StyledWrapper>
                )}
            </CartLineContainer.Description>
            <CartLineContainer.Price>
                <ProductPrice
                    product={cartLine}
                    showSavings={true}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    showInvalidPriceMessage={showInvalidPriceMessage}
                    extendedStyles={styles.price}
                    // XNG-Changes
                    {...{ showQuoteTooltip: false, showPricingUom: true, showSalePriceLabel: false }}
                />
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {/* XNG-Changes: show - - when no value */}
                {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) ? (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <>
                                <SmallHeadingAndText
                                    heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                    text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                    extendedStyles={styles.subtotalWithoutVatText}
                                />
                                <SmallHeadingAndText
                                    heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                    text={cartLine.pricing.vatAmountDisplay}
                                    extendedStyles={styles.vatAmount}
                                />
                            </>
                        )}
                        <SmallHeadingAndText
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                      ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                      : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                            }
                            extendedStyles={styles.extendedUnitNetPrice}
                        />
                    </>
                ) : (
                    <SmallHeadingAndText {...styles.extendedUnitNetPrice} heading={translate("Total")} text="– –" />
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Notes>
                {cartLine.notes && (
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        // label={translate("Line Notes")}
                        extendedStyles={styles.cartLineNotes}
                    />
                )}
            </CartLineContainer.Notes>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardExpanded));
